import {
  ANALYTICS_EVENTS,
  posthog,
  registerAnalyticsContext,
  unregisterAnalyticsContext
} from '@kiku/analytics/client';
import type { JobStatus } from '@kiku/jobs/types';
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { InferResponseType } from 'hono/client';
import {
  AlertCircle,
  ChevronDown,
  Download,
  Loader2,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  Users
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { ApplicationKanban } from '~/components/features/applications/application-kanban';
import { CreateApplicationDialog } from '~/components/features/applications/create-application-dialog';
import { DepartmentBreadcrumb } from '~/components/features/departments/department-breadcrumb';
import { generateCsv, ResultsTable } from '~/components/features/results/results-table';
import { PageContainer } from '~/components/layouts/page-container';
import { Alert, AlertDescription } from '~/components/ui/alert';
import { Badge } from '~/components/ui/badge';
import { buttonVariants } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '~/components/ui/dropdown-menu';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText
} from '~/components/ui/input-group';
import { Skeleton } from '~/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { useDocumentTitle } from '~/hooks/use-document-title';
import { useSlugIds } from '~/hooks/use-slug-ids';
import { api } from '~/lib/api';
import { meQueryOptions } from '~/lib/auth';
import { i18n } from '~/lib/i18n';
import { resolveSlugIds } from '~/lib/slug-resolver';
import { cn } from '~/lib/utils';
import { stagesQueryOptions } from '~/lib/workflow-stages';
import { jobDetailQueryOptions } from '~/queries/jobs.queries';

const JOB_DETAIL_TABS = ['candidates', 'results', 'settings'] as const;
type JobDetailTab = (typeof JOB_DETAIL_TABS)[number];

const jobDetailSearchSchema = z.object({
  tab: z.enum(JOB_DETAIL_TABS).optional().default('candidates')
});

type ApplicationsResponse = InferResponseType<
  (typeof api.api.org)[':orgId']['department'][':deptId']['jobs'][':jobId']['applications']['$get']
>;
type ApplicationItem = ApplicationsResponse['applications'][number];
type QuestionsResponse = InferResponseType<
  (typeof api.api.org)[':orgId']['department'][':deptId']['jobs'][':jobId']['interview-setup']['questions']['$get']
>;
type QuestionItem = QuestionsResponse['questions'][number];

function applicationsQueryOptions(orgId: string, deptId: string, jobId: string) {
  return queryOptions({
    queryKey: ['applications', orgId, deptId, jobId] as const,
    queryFn: async () => {
      const response = await api.api.org[':orgId'].department[':deptId'].jobs[
        ':jobId'
      ].applications.$get({
        param: { orgId, deptId, jobId },
        query: {}
      });
      if (!response.ok) throw new Error(i18n.t('errors.unexpected'));
      return response.json();
    }
  });
}

function questionsQueryOptions(orgId: string, deptId: string, jobId: string) {
  return queryOptions({
    queryKey: ['interview-questions', orgId, deptId, jobId] as const,
    queryFn: async () => {
      const response = await api.api.org[':orgId'].department[':deptId'].jobs[':jobId'][
        'interview-setup'
      ].questions.$get({
        param: { orgId, deptId, jobId }
      });
      if (!response.ok) throw new Error(i18n.t('errors.unexpected'));
      return response.json();
    }
  });
}

function resultsQueryOptions(orgId: string, deptId: string, jobId: string) {
  return queryOptions({
    queryKey: ['job-results', orgId, deptId, jobId] as const,
    queryFn: async () => {
      const response = await api.api.org[':orgId'].department[':deptId'].jobs[
        ':jobId'
      ].results.$get({ param: { orgId, deptId, jobId } });
      if (!response.ok) throw new Error(i18n.t('errors.unexpected'));
      return response.json();
    }
  });
}

export const Route = createFileRoute(
  '/_authenticated/org/$orgSlug/department/$deptSlug/jobs/$jobId/'
)({
  validateSearch: jobDetailSearchSchema,
  loader: ({ context, params }) => {
    const { orgId, deptId } = resolveSlugIds(context.queryClient, params.orgSlug, params.deptSlug);
    if (orgId && deptId) {
      void context.queryClient.prefetchQuery(jobDetailQueryOptions(orgId, deptId, params.jobId));
      void context.queryClient.prefetchQuery(applicationsQueryOptions(orgId, deptId, params.jobId));
      void context.queryClient.prefetchQuery(questionsQueryOptions(orgId, deptId, params.jobId));
      void context.queryClient.prefetchQuery(resultsQueryOptions(orgId, deptId, params.jobId));
    }
  },
  component: JobDetailPage
});

function JobDetailPage(): React.ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { orgSlug, deptSlug, jobId } = Route.useParams();
  const { tab } = Route.useSearch();
  const { orgId, deptId } = useSlugIds(orgSlug, deptSlug);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState('');

  useEffect(() => {
    if (!deptId) return;
    registerAnalyticsContext({ deptId, jobId });
    return () => {
      unregisterAnalyticsContext(['deptId', 'jobId']);
    };
  }, [deptId, jobId]);

  const { data: meData } = useQuery(meQueryOptions);
  const { data: jobData } = useQuery({
    ...jobDetailQueryOptions(orgId, deptId ?? '', jobId),
    enabled: !!orgId && !!deptId
  });
  const { data: applicationsData } = useQuery({
    ...applicationsQueryOptions(orgId, deptId ?? '', jobId),
    placeholderData: keepPreviousData,
    enabled: !!orgId && !!deptId
  });
  const { data: stagesData } = useQuery({
    ...stagesQueryOptions(orgId),
    enabled: !!orgId
  });
  const { data: questionsData } = useQuery({
    ...questionsQueryOptions(orgId, deptId ?? '', jobId),
    enabled: !!orgId && !!deptId
  });
  const { data: resultsData } = useQuery({
    ...resultsQueryOptions(orgId, deptId ?? '', jobId),
    enabled: !!orgId && !!deptId
  });

  const resyncMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.org[':orgId'].department[':deptId'].jobs[':jobId'][
        'resync-applicants'
      ].$post({ param: { orgId, deptId: deptId ?? '', jobId } });
      if (!res.ok) throw new Error(i18n.t('errors.unexpected'));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['jobs', orgId, deptId, jobId] });
    }
  });

  const addDemoApplicantMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.org[':orgId'].department[':deptId'].jobs[':jobId'].applications[
        'add-demo-applicant'
      ].$post({ param: { orgId, deptId: deptId ?? '', jobId } });
      if (!res.ok) throw new Error(i18n.t('errors.unexpected'));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['applications', orgId, deptId, jobId] });
    }
  });

  const job = jobData?.job;
  useDocumentTitle(job?.title);
  const allApplications = applicationsData?.applications ?? [];
  const applications = useMemo(() => {
    const query = candidateSearch.trim().toLowerCase();
    if (!query) return allApplications;
    return allApplications.filter(({ candidate }) => {
      const phone = candidate.phoneCountryCode
        ? `${candidate.phoneCountryCode} ${candidate.phoneNumber ?? ''}`
        : (candidate.phoneNumber ?? '');
      return (
        candidate.name.toLowerCase().includes(query) ||
        candidate.email.toLowerCase().includes(query) ||
        phone.toLowerCase().includes(query)
      );
    });
  }, [allApplications, candidateSearch]);
  const stages = stagesData?.stages ?? applicationsData?.stages ?? [];
  const questions = questionsData?.questions ?? [];
  const results = resultsData?.results ?? [];
  const isLoading = !jobData;
  const isAdmin = meData?.role === 'admin';

  const syncStatus = job?.applicantSyncStatus;
  const isSyncing = syncStatus === 'pending' || syncStatus === 'syncing';
  const providerLabel = job?.atsProvider
    ? t(`integrations.ats.providers.${job.atsProvider}`)
    : null;

  const jobStatusVariant = (status: JobStatus | undefined): 'default' | 'outline' => {
    return status === 'active' ? 'default' : 'outline';
  };

  const stageChangeMutation = useMutation({
    mutationFn: async ({ applicationId, stageId }: { applicationId: string; stageId: string }) => {
      const response = await api.api.org[':orgId'].department[':deptId'].jobs[
        ':jobId'
      ].applications[':applicationId'].stage.$patch({
        param: { orgId, deptId: deptId ?? '', jobId, applicationId },
        json: { stageId }
      });
      if (!response.ok) throw new Error(i18n.t('errors.unexpected'));
      return response.json();
    },
    onMutate: async ({ applicationId, stageId }) => {
      const queryKey = ['applications', orgId, deptId ?? '', jobId] as const;
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ApplicationsResponse>(queryKey);
      const fromStageId =
        previous?.applications.find((a) => a.id === applicationId)?.stageId ?? null;
      if (previous) {
        queryClient.setQueryData(queryKey, {
          ...previous,
          applications: previous.applications.map((a) =>
            a.id === applicationId ? { ...a, stageId } : a
          )
        });
      }
      return { previous, fromStageId };
    },
    onSuccess: (_result, { applicationId, stageId }, context) => {
      posthog.capture(ANALYTICS_EVENTS.CANDIDATE_STAGE_CHANGED, {
        application_id: applicationId,
        from_stage_id: context.fromStageId,
        to_stage_id: stageId,
        source: 'kanban'
      });
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        const queryKey = ['applications', orgId, deptId ?? '', jobId] as const;
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ['applications', orgId, deptId ?? '', jobId]
      });
    }
  });

  const handleStageChange = useCallback(
    (applicationId: string, stageId: string) => {
      stageChangeMutation.mutate({ applicationId, stageId });
    },
    [stageChangeMutation]
  );

  const handleApplicationClick = useCallback(
    (application: ApplicationItem) => {
      void navigate({
        to: '/org/$orgSlug/department/$deptSlug/jobs/$jobId/$applicationId',
        params: { orgSlug, deptSlug, jobId, applicationId: application.id }
      });
    },
    [navigate, orgSlug, deptSlug, jobId]
  );

  function setTab(newTab: JobDetailTab): void {
    void navigate({
      to: '/org/$orgSlug/department/$deptSlug/jobs/$jobId',
      params: { orgSlug, deptSlug, jobId },
      search: (prev) => ({ ...prev, tab: newTab })
    });
  }

  function handleExportCsv(): void {
    const csv = generateCsv(results, stages, questions, job?.title ?? '');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(job?.title ?? 'results').replace(/\s+/g, '-').toLowerCase()}-results.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <PageContainer
      title={job?.title ?? ''}
      subtitle={job?.location ?? ''}
      badge={
        job?.status && (
          <Badge variant={jobStatusVariant(job.status)}>{t(`jobs.status.${job.status}`)}</Badge>
        )
      }
      breadcrumbs={[
        { content: <DepartmentBreadcrumb orgSlug={orgSlug} deptSlug={deptSlug} /> },
        {
          label: t('jobs.title'),
          href: `/org/${orgSlug}/department/${deptSlug}/jobs`
        },
        { label: job?.title ?? '' }
      ]}
      actions={
        tab === 'candidates' ? (
          <div className="flex items-center gap-2">
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger
                  className={cn(buttonVariants({ variant: 'outline' }), 'gap-1.5')}
                >
                  {t('jobs.applicantSync.adminActions')}
                  <ChevronDown className="size-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  {job?.atsExternalJobId && (
                    <DropdownMenuItem
                      onClick={() => {
                        resyncMutation.mutate();
                      }}
                      disabled={resyncMutation.isPending || isSyncing}
                      className="flex flex-col items-start gap-0.5"
                    >
                      <div className="flex items-center gap-2">
                        {resyncMutation.isPending ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <RefreshCw className="size-4" />
                        )}
                        <span className="font-semibold">{t('jobs.applicantSync.resyncLabel')}</span>
                      </div>
                      {job.lastApplicantSyncAt && (
                        <span className="pl-6 text-xs text-muted-foreground">
                          {t('jobs.applicantSync.lastSyncedAt', {
                            date: new Date(job.lastApplicantSyncAt).toLocaleString()
                          })}
                        </span>
                      )}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => {
                      addDemoApplicantMutation.mutate();
                    }}
                    disabled={addDemoApplicantMutation.isPending}
                  >
                    {addDemoApplicantMutation.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Plus className="size-4" />
                    )}
                    {t('jobs.applicantSync.addDemoApplicant')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger
                className={cn(
                  buttonVariants(),
                  'w-8 px-0 bg-transparent text-foreground hover:bg-muted',
                  'sm:w-auto sm:px-2.5 sm:bg-primary sm:text-primary-foreground sm:hover:bg-primary/90'
                )}
              >
                <MoreHorizontal className="size-4 sm:hidden" />
                <span className="hidden sm:inline">{t('applications.newCandidate')}</span>
                <ChevronDown className="hidden sm:inline size-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onClick={() => {
                    setCreateDialogOpen(true);
                  }}
                >
                  <Plus className="size-4" />
                  {t('applications.addCandidate')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : tab === 'results' ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              className={cn(
                buttonVariants(),
                'w-8 px-0 bg-transparent text-foreground hover:bg-muted',
                'sm:w-auto sm:px-2.5 sm:bg-primary sm:text-primary-foreground sm:hover:bg-primary/90'
              )}
            >
              <MoreHorizontal className="size-4 sm:hidden" />
              <span className="hidden sm:inline">{t('results.exportLabel')}</span>
              <ChevronDown className="hidden sm:inline size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={handleExportCsv}>
                <Download className="size-4" />
                {t('results.exportCsv')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null
      }
    >
      {/* Tabs */}
      <Tabs
        value={tab}
        onValueChange={(value) => {
          setTab(value as JobDetailTab);
        }}
      >
        <TabsList variant="line" className="w-full sm:w-fit sm:self-start">
          {JOB_DETAIL_TABS.map((t_) => (
            <TabsTrigger
              key={t_}
              value={t_}
              className="data-active:text-primary data-active:after:bg-primary"
            >
              {t(`jobs.detail.tabs.${t_}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Candidate search */}
      {tab === 'candidates' && allApplications.length > 0 && (
        <InputGroup className="max-w-sm">
          <InputGroupAddon align="inline-start">
            <InputGroupText>
              <Search className="size-4" />
            </InputGroupText>
          </InputGroupAddon>
          <InputGroupInput
            className="!text-sm"
            placeholder={t('applications.searchPlaceholder')}
            value={candidateSearch}
            onChange={(e) => {
              setCandidateSearch(e.target.value);
            }}
          />
        </InputGroup>
      )}

      {/* Candidates tab */}
      {tab === 'candidates' && (
        <>
          {!isLoading && isSyncing && providerLabel && (
            <Alert className="border-blue-200 bg-blue-50 text-blue-900">
              <Loader2 className="size-4 animate-spin text-blue-600" />
              <AlertDescription className="text-blue-800">
                {t('jobs.applicantSync.syncingBanner', { provider: providerLabel })}
              </AlertDescription>
            </Alert>
          )}

          {!isLoading && syncStatus === 'failed' && (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertDescription>{t('jobs.applicantSync.failedBanner')}</AlertDescription>
            </Alert>
          )}

          {!isLoading && allApplications.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-4 py-20">
              <div className="flex size-14 items-center justify-center rounded-full bg-muted">
                <Users className="size-7 text-muted-foreground" />
              </div>
              <div className="flex flex-col items-center gap-1 text-center">
                <p className="text-base font-semibold text-foreground">
                  {t('applications.emptyTitle')}
                </p>
                <p className="max-w-sm text-sm text-muted-foreground">
                  {t('applications.emptyDescription')}
                </p>
              </div>
            </div>
          ) : !isLoading && applications.length === 0 && candidateSearch ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              {t('applications.emptySearchDescription')}
            </p>
          ) : (
            <ApplicationKanban
              applications={applications}
              stages={stages}
              onApplicationClick={handleApplicationClick}
              onStageChange={handleStageChange}
            />
          )}
        </>
      )}

      {/* Results tab */}
      {tab === 'results' && (
        <ResultsTable
          results={results}
          questions={questions}
          orgSlug={orgSlug}
          deptSlug={deptSlug}
          jobId={jobId}
        />
      )}

      {/* Settings tab */}
      {tab === 'settings' && (
        <div className="flex flex-col gap-6">
          <JobInfoSection job={job} isLoading={isLoading} />
          <hr className="border-border" />
          <QuestionsSection questions={questions} isLoading={!questionsData} />
        </div>
      )}

      {deptId && (
        <CreateApplicationDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          orgId={orgId}
          deptId={deptId}
          jobId={jobId}
        />
      )}
    </PageContainer>
  );
}

type JobInfoSectionProps = {
  job:
    | {
        title: string;
        description: string | null;
        employer: string | null;
        location: string | null;
        modality: Array<'text' | 'voice'>;
        languages: string[];
        atsProvider: string | null;
        atsExternalJobId: string | null;
      }
    | undefined;
  isLoading: boolean;
};

function JobInfoSection({ job, isLoading }: JobInfoSectionProps): React.ReactNode {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-5 w-40" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-1">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-5 w-48" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">{t('jobs.detail.info.title')}</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {job?.employer && (
          <div className="flex flex-col gap-1">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t('jobs.detail.info.employer')}
            </p>
            <p className="text-sm">{job.employer}</p>
          </div>
        )}
        {job?.location && (
          <div className="flex flex-col gap-1">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t('jobs.detail.info.location')}
            </p>
            <p className="text-sm">{job.location}</p>
          </div>
        )}
        {job?.modality && job.modality.length > 0 && (
          <div className="flex flex-col gap-1">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t('jobs.detail.info.modality')}
            </p>
            <div className="flex gap-2">
              {job.modality.map((m) => (
                <Badge key={m} variant="outline">
                  {m === 'text'
                    ? t('wizard.basicInfo.modalityText')
                    : t('wizard.basicInfo.modalityVoice')}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {job?.languages && job.languages.length > 0 && (
          <div className="flex flex-col gap-1">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t('jobs.detail.info.languages')}
            </p>
            <p className="text-sm">
              {job.languages.map((lang) => t(`screening.languages.${lang}`, lang)).join(', ')}
            </p>
          </div>
        )}
        {job?.atsProvider && (
          <div className="flex flex-col gap-1">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t('jobs.detail.info.atsProvider')}
            </p>
            <p className="text-sm">{job.atsProvider}</p>
          </div>
        )}
        {job?.atsExternalJobId && (
          <div className="flex flex-col gap-1">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t('jobs.detail.info.atsId')}
            </p>
            <p className="text-sm font-mono">{job.atsExternalJobId}</p>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {t('jobs.detail.info.description')}
        </p>
        {job?.description ? (
          <div className="job-description" dangerouslySetInnerHTML={{ __html: job.description }} />
        ) : (
          <p className="text-sm text-muted-foreground">{t('jobs.detail.info.noDescription')}</p>
        )}
      </div>
    </div>
  );
}

type QuestionsSectionProps = {
  questions: QuestionItem[];
  isLoading: boolean;
};

function QuestionsSection({ questions, isLoading }: QuestionsSectionProps): React.ReactNode {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-5 w-40" />
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  const CATEGORY_ORDER = ['eligibility', 'job_expectations', 'assessment', 'information'] as const;
  type QuestionCategory = (typeof CATEGORY_ORDER)[number];

  const CATEGORY_COLORS: Record<QuestionCategory, string> = {
    eligibility: 'border-l-[var(--color-section-blue)]',
    job_expectations: 'border-l-[var(--color-section-green)]',
    assessment: 'border-l-[var(--color-section-orange)]',
    information: 'border-l-[var(--color-section-purple)]'
  };

  const grouped = CATEGORY_ORDER.map((category) => ({
    category,
    items: questions.filter((q) => q.category === category)
  })).filter(({ items }) => items.length > 0);

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">{t('jobs.detail.questions.title')}</h2>
      {questions.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('jobs.detail.questions.empty')}</p>
      ) : (
        grouped.map(({ category, items }) => (
          <div key={category} className="flex flex-col gap-2">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t(`jobs.detail.questions.categories.${category}`)}
            </p>
            <div className="flex flex-col gap-2">
              {items.map((q) => (
                <div
                  key={q.id}
                  className={cn(
                    'flex items-start justify-between rounded-md border border-border border-l-4 px-4 py-3',
                    CATEGORY_COLORS[category]
                  )}
                >
                  <p className="text-sm">{q.text}</p>
                  <div className="ml-4 flex shrink-0 items-center gap-2">
                    {q.competencyName && (
                      <Badge variant="outline" className="text-xs">
                        {q.competencyName}
                      </Badge>
                    )}
                    {q.isOptional && (
                      <Badge variant="outline" className="text-xs text-muted-foreground">
                        {t('jobs.detail.questions.optional')}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
